import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { TRPCClientError } from "@trpc/client";
import { trpc } from "@/trpc/client";
import { PostEditor } from "#/components/publish/PostEditor";
import { Card, EmptyState, PageHeading } from "@/ui";
import { AppLink } from "@/ui/AppLink";

// Matches `Button variant="secondary"`'s look for a link the ui-kit's
// button-only component can't render — same pattern as `PostList.tsx`'s
// "Write your first post" action.
const secondaryLinkButton =
  "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border";
const secondaryLinkButtonStyle = {
  borderRadius: "var(--gs-radius)",
  background: "var(--gs-surface)",
  borderColor: "var(--gs-border)",
};

/**
 * The honest text for a `getPost` failure (code review, Task 5 follow-up).
 * `NOT_FOUND` — a deleted post, or one that belongs to a different workspace
 * — gets its own copy; every other failure (network blip, a publish-disabled
 * workspace's FORBIDDEN, …) gets a generic one rather than a wrong guess.
 */
function editPostErrorCopy(err: unknown): { title: string; body: string } {
  const code =
    err instanceof TRPCClientError
      ? (err.data as { code?: unknown } | undefined)?.code
      : undefined;
  if (code === "NOT_FOUND") {
    return {
      title: "This post doesn't exist here",
      body: "It may have been deleted, or it belongs to a different workspace than the one you're viewing.",
    };
  }
  return {
    title: "Couldn't load this post",
    body: "Something went wrong loading this draft. Check your connection and try again, or head back to the list.",
  };
}

/**
 * Editing a post (design §3). Until now this was not a route at all:
 * `PublishPage` held `useState<{ postId } | null>` and swapped the list for
 * the editor in place, so an open editor had no URL, survived no refresh, and
 * could not be linked to.
 *
 * `/posts/new` and `/posts/$postId` do not collide — TanStack ranks static
 * segments above dynamic ones, so `/posts/new` always wins. Pinned by
 * `dashboard-route-parity.test.tsx`'s "unshadowed by any dynamic sibling".
 *
 * No `usePublishGate` check here, unlike `NewPostPage`. `PublishPage`'s gate
 * used to cover the edit branch by sitting above it, so moving edit onto its
 * own route drops that. Deliberate: the per-workspace publish gate is being
 * removed separately (design §"Out of scope"), and gating an EXISTING post's
 * editor is the weaker case anyway — the post is already there.
 *
 * This route DOES hold `PostEditor` back until `getPost` settles, though
 * (code review, Task 5 follow-up) — same pattern `NewPostPage` uses for its
 * publish gate. `PostEditor` reads `postQuery.data` only: on a failed load
 * the seed effect never runs, so it would render a blank, fully-editable
 * composer — title empty, markdown empty, indistinguishable from a genuinely
 * new draft, and one keystroke away from autosaving "" over the real post's
 * body. (`PostEditor`'s debounce carries the same `postQuery.data` guard for
 * exactly that reason, so the two agree rather than one relying on the other.)
 * Before
 * this route existed `postId` only ever came from a just-rendered list in the
 * current workspace, so `getPost`'s `NOT_FOUND` (deleted post, or one in
 * another workspace) was effectively unreachable; now it comes from a URL
 * that survives a refresh and can be bookmarked, so a cold load failing is
 * the normal case, not an edge one.
 *
 * The query here uses the exact same `queryOptions` shape `PostEditor` itself
 * builds (`getPost.queryOptions({ workspaceId, postId })`) — React Query's
 * key hashing is order-insensitive, so a successful settle here serves
 * `PostEditor`'s own `useQuery` straight from cache. No double fetch.
 *
 * No route `loader` for this. A loader's pending window lives in
 * `state.pendingMatches`, not `state.matches` — `useFullscreenRoute` reads
 * `state.matches`, so the OLD route's chrome would still be showing while a
 * loader was pending, and any `pendingComponent` would render inside it.
 */
export const Route = createFileRoute("/dashboard/w/$workspaceId/posts/$postId")({
  staticData: { fullscreen: true },
  component: EditPostRoute,
});

function EditPostRoute() {
  const { workspaceId, postId } = Route.useParams();
  const navigate = Route.useNavigate();
  const postQuery = useQuery(
    trpc.authenticatedRouterFunction.getPost.queryOptions({ workspaceId, postId }),
  );

  if (postQuery.isPending || postQuery.isError) {
    const errorCopy = postQuery.isError ? editPostErrorCopy(postQuery.error) : null;
    // One live region wrapping both the pending line and the outcome (same
    // shape as `NewPostPage`'s publish gate): a live region announces
    // *subsequent* mutations, not the content it was registered with, so
    // unmounting it and mounting a fresh one for the error state would
    // announce neither.
    return (
      // Its own scroll region, because the fullscreen frame around it now
      // clips (`WorkspaceChrome`: `lg:h-[100dvh] lg:overflow-hidden`). Without
      // this, a short viewport would cut off the error `EmptyState` and its
      // "Back to Posts" link — the only way off this branch, which mounts no
      // `PostEditor` and so has no × either.
      <div className="flex flex-col gap-6 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        <PageHeading title="Edit post" />
        <div role="status">
          {errorCopy ? (
            <EmptyState
              title={errorCopy.title}
              body={errorCopy.body}
              action={
                <AppLink
                  to="/dashboard/w/$workspaceId/posts"
                  params={{ workspaceId }}
                  className={secondaryLinkButton}
                  style={secondaryLinkButtonStyle}
                >
                  Back to Posts
                </AppLink>
              }
            />
          ) : (
            <Card>
              <p className="text-sm" style={{ color: "var(--gs-muted)" }}>
                Loading…
              </p>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <PostEditor
      // Remount when the edited post changes so the editor reseeds its
      // once-seeded local state instead of showing the previous post.
      key={postId}
      workspaceId={workspaceId}
      postId={postId}
      onClose={() =>
        void navigate({
          to: "/dashboard/w/$workspaceId/posts",
          params: { workspaceId },
        })
      }
    />
  );
}
